import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Resource, RESOURCE_TYPES } from '../types';
import { Modal } from '../components/common/Modal';
import { FileUpload } from '../components/common/FileUpload';
import { Package, Pencil, Trash2 } from 'lucide-react';
import { useT } from '../i18n';

export function ResourcesPage() {
  const { t } = useT();
  const { isAdmin, user } = useAuth();
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [filterFunction, setFilterFunction] = useState<string>('');
  const [functions, setFunctions] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'Human' as Resource['type'],
    function: '',
  });

  useEffect(() => {
    loadResources();
    loadFunctions();
  }, []);

  const loadResources = async () => {
    try {
      const data = await api.getResources() as Resource[];
      setResources(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFunctions = async () => {
    try {
      const data = await api.getResourceFunctions() as string[];
      setFunctions(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingResource) {
        await api.updateResource(editingResource.id, form);
      } else {
        await api.createResource(form);
      }
      setShowModal(false);
      setEditingResource(null);
      setForm({ title: '', description: '', type: 'Human', function: '' });
      loadResources();
      loadFunctions();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleEdit = (resource: Resource) => {
    setEditingResource(resource);
    setForm({
      title: resource.title,
      description: resource.description,
      type: resource.type,
      function: resource.function,
    });
    setShowModal(true);
  };

  const handleDelete = async (resource: Resource) => {
    if (!confirm(t('res.confirmDelete').replace('{title}', resource.title))) return;
    try {
      await api.deleteResource(resource.id);
      loadResources();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleUpload = async (data: any[]) => {
    try {
      const result = await api.uploadResources(data) as { success: boolean; inserted: number; errors: any[] };
      if (result.errors.length > 0) {
        alert(t('res.uploadResult').replace('{n}', String(result.inserted)).replace('{e}', String(result.errors.length)));
      }
      setShowUpload(false);
      loadResources();
      loadFunctions();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    const confirmMsg = count > 1
      ? t('res.confirmBulkMany').replace('{n}', String(count))
      : t('res.confirmBulkOne');
    if (!confirm(confirmMsg)) return;

    setDeleting(true);
    try {
      await api.bulkDeleteResources(Array.from(selectedIds));
      setSelectedIds(new Set());
      loadResources();
      loadFunctions();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredResources.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredResources.map((r) => r.id)));
    }
  };

  const toggleSelect = (id: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const filteredResources = resources.filter((r) => {
    if (filterType && r.type !== filterType) return false;
    if (filterFunction && r.function !== filterFunction) return false;
    return true;
  });

  const canEdit = (resource: Resource) => isAdmin || resource.createdBy === user?.id;

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="card-header mb-16">
        <div className="flex gap-8">
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filterType}
            onChange={(e) => { setFilterType(e.target.value); setSelectedIds(new Set()); }}
          >
            <option value="">{t('res.allTypes')}</option>
            {RESOURCE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            className="form-select"
            style={{ width: 'auto' }}
            value={filterFunction}
            onChange={(e) => { setFilterFunction(e.target.value); setSelectedIds(new Set()); }}
          >
            <option value="">{t('res.allFunctions')}</option>
            {functions.map((func) => (
              <option key={func} value={func}>
                {func}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-8">
          {isAdmin && selectedIds.size > 0 && (
            <button
              className="btn btn-danger"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? t('res.deleting') : t('res.deleteCount').replace('{n}', String(selectedIds.size))}
            </button>
          )}
          {isAdmin && (
            <button className="btn btn-ghost" onClick={() => setShowUpload(true)}>
              {t('csv.uploadCsv')}
            </button>
          )}
          <button
            className="btn btn-accent"
            onClick={() => {
              setEditingResource(null);
              setForm({ title: '', description: '', type: 'Human', function: '' });
              setShowModal(true);
            }}
          >
            {t('res.addResource')}
          </button>
        </div>
      </div>

      {filteredResources.length === 0 ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><Package /></div>
            <div className="empty-state-title">{t('res.emptyTitle')}</div>
            <div className="empty-state-desc">
              {isAdmin ? t('res.emptyDescAdmin') : t('res.emptyDescUser')}
            </div>
          </div>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              {isAdmin && (
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={filteredResources.length > 0 && selectedIds.size === filteredResources.length}
                    onChange={toggleSelectAll}
                    title={t('res.selectAll')}
                  />
                </th>
              )}
              <th>{t('res.colTitle')}</th>
              <th>{t('res.colType')}</th>
              <th>{t('res.colFunction')}</th>
              <th>{t('res.colDescription')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredResources.map((resource) => (
              <tr key={resource.id}>
                {isAdmin && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(resource.id)}
                      onChange={() => toggleSelect(resource.id)}
                    />
                  </td>
                )}
                <td className="font-medium">{resource.title}</td>
                <td>
                  <span className={`badge badge-${resource.type.toLowerCase()}`}>{resource.type}</span>
                </td>
                <td>{resource.function}</td>
                <td className="text-muted">{resource.description?.slice(0, 100)}{resource.description?.length > 100 ? '...' : ''}</td>
                <td>
                  {canEdit(resource) && (
                    <div className="flex gap-8">
                      <button className="btn-icon" onClick={() => handleEdit(resource)} title={t('common.edit')}>
                        <Pencil className="icon-sm" />
                      </button>
                      <button className="btn-icon" onClick={() => handleDelete(resource)} title={t('common.delete')}>
                        <Trash2 className="icon-sm" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <Modal
          title={editingResource ? t('res.editResource') : t('res.addResource')}
          onClose={() => {
            setShowModal(false);
            setEditingResource(null);
          }}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">{t('res.labelTitle')}</label>
              <input
                type="text"
                className="form-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t('res.labelType')}</label>
              <select
                className="form-select"
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value as Resource['type'] })}
                required
              >
                {RESOURCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('res.labelFunction')}</label>
              <input
                type="text"
                className="form-input"
                value={form.function}
                onChange={(e) => setForm({ ...form, function: e.target.value })}
                placeholder={t('res.functionPlaceholder')}
                list="functions"
                required
              />
              <datalist id="functions">
                {functions.map((func) => (
                  <option key={func} value={func} />
                ))}
              </datalist>
            </div>
            <div className="form-group">
              <label className="form-label">{t('res.labelDescription')}</label>
              <textarea
                className="form-textarea"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="modal-footer" style={{ padding: 0, border: 'none', marginTop: 'var(--s16)' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-accent">
                {editingResource ? t('common.save') : t('res.create')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {showUpload && (
        <Modal title={t('res.uploadModalTitle')} onClose={() => setShowUpload(false)}>
          <FileUpload
            expectedHeaders={['Title', 'Description', 'Type', 'Function']}
            onUpload={handleUpload}
            onCancel={() => setShowUpload(false)}
          />
        </Modal>
      )}
    </div>
  );
}
